export enum CalculatorType {
  TILE = 'tile',
  CONCRETE = 'concrete',
  FLOORING = 'flooring',
  FRAMING = 'framing',
  PAINT = 'paint',
  RETAINING_WALL = 'retaining_wall',
  ELECTRICAL = 'electrical',
  PLUMBING = 'plumbing',
}

export interface CalculatorOption {
  value: CalculatorType;
  label: string;
  description: string;
  icon?: string;
}

export interface MessageState {
  message: string;
  type: 'error' | 'success';
}

// Tile Calculator Types
export interface TileCalculatorInputs {
  roomLength: string;
  roomWidth: string;
  tileLength: string;
  tileWidth: string;
  groutJoint: string;
  wastePercentage: number;
  tilesPerBox: string;
}

export interface TileCalculatorResults {
  roomArea: string;
  tilesNeeded: number;
  groutNeeded: string;
  boxesNeeded: number;
}

// Concrete Calculator Types
export enum ConcreteType {
  SLAB = 'slab',
  SIDEWALK = 'sidewalk',
  FOOTER = 'footer',
  STAIRS = 'stairs',
  COLUMN = 'column',
  TUBE = 'tube',
  CURB = 'curb',
  GUTTER_BARRIER = 'gutter-barrier',
}

export interface ConcreteCalculatorInputs {
  calcType: ConcreteType;
  length: string;
  width: string;
  thickness: string;
  height: string;
  diameter: string;
  side1: string;
  side2: string;
  numSteps: string;
  riserHeight: string;
  treadDepth: string;
  stepWidth: string;
  columnShape: 'round' | 'square';
  curbDepth: string;
  gutterWidth: string;
  curbHeight: string;
  flagThickness: string;
  tubeWallThickness: string;
}

export interface ConcreteCalculatorResults {
  result: string;
}

// Flooring Calculator Types
export interface FlooringCalculatorInputs {
  roomLength: string;
  roomWidth: string;
  wastePercentage: number;
  sqftPerBox: string;
  plankLength: string;
  plankWidth: string;
}

export interface FlooringCalculatorResults {
  roomArea: string;
  flooringNeeded: string;
  boxesNeeded: number;
  planksNeeded: number | null;
}

// Framing Calculator Types
export interface FramingCalculatorInputs {
  wallLength: string;
  studSpacing: number;
  numCorners: number;
  numDoors: number;
  numWindows: number;
}

export interface FramingCalculatorResults {
  studsNeeded: number;
  platesNeeded: string;
}

// Paint Calculator Types
export interface PaintCalculatorInputs {
  roomLength: string;
  roomWidth: string;
  roomHeight: string;
  numDoors: string;
  numWindows: string;
  coveragePerGallon: number;
  numCoats: number;
}

export interface PaintCalculatorResults {
  totalSurfaceArea: string;
  paintNeeded: number;
}

// Retaining Wall Calculator Types
export interface RetainingWallCalculatorInputs {
  wallLength: string;
  wallHeight: string;
  blockLength: string;
  blockHeight: string;
  hasCapRow: boolean;
  backfillLength: string;
  backfillHeight: string;
  backfillThickness: string;
}

export interface RetainingWallCalculatorResults {
  totalBlocks: number;
  totalWallBlocks: number;
  totalCapBlocks: number;
  totalGravel: string;
  gravelWeight: string;
}

// Electrical Calculator Types
export type WireMaterial = 'Copper' | 'Aluminum';
export type WireType = 'THHN' | 'Romex';
export type ProtectionType = 'Standard' | 'GFCI' | 'AFCI' | 'Dual';
export type AmbientTemp =
  | 'Standard (86°F)'
  | 'Warm (104°F)'
  | 'Hot Attic (122°F)'
  | 'Extreme (140°F)';
export type WireGauge =
  | '14'
  | '12'
  | '10'
  | '8'
  | '6'
  | '4'
  | '2'
  | '1/0'
  | '2/0'
  | '4/0';

export interface ElectricalRun {
  id: number;
  label: string;
  material: WireMaterial;
  voltage: number;
  current: string;
  wireGauge: WireGauge;
  runLength: string;
  type: WireType;
  protection: ProtectionType;
  ambientTemp: AmbientTemp;
}

export interface ElectricalRunResult extends ElectricalRun {
  dropPct: number;
  deratedAmp: number;
  conductors: number;
  minConduit: string;
  isSafe: boolean;
  breakerSize: number;
}

export interface ElectricalCalculatorInputs {
  runs: ElectricalRun[];
  maxDropAllowed: number;
}

export interface WireInventoryItem {
  item: string;
  quantity_feet: number;
}

export interface BreakerInventoryItem {
  item: string;
  quantity: number;
}

export interface ConduitInventoryItem {
  size: string;
  total_feet: number;
  sticks_10ft: number;
}

export interface ElectricalBOMSummary {
  total_runs: number;
  total_wire_ft: number;
}

export interface ElectricalCalculatorResults {
  runs: ElectricalRunResult[];
  bom: {
    summary: ElectricalBOMSummary;
    wire_inventory: WireInventoryItem[];
    breaker_inventory: BreakerInventoryItem[];
    conduit_inventory: ConduitInventoryItem[];
  };
}

// Plumbing Calculator Types
export type PipeMaterial = 'PEX' | 'Copper_L';
export type PipeSize = '1/2"' | '3/4"' | '1"';
export type FixtureType =
  | 'Toilet (HE)'
  | 'Shower'
  | 'Kitchen Sink'
  | 'Lavatory'
  | 'Garden Tub';

export interface PlumbingRun {
  id: number;
  label: string;
  material: PipeMaterial;
  size: PipeSize;
  length: string;
  fixture: FixtureType;
  count: string;
  elbows90: string;
  elbows45: string;
  teesLine: string;
  teesBranch: string;
}

export interface PlumbingRunResult extends PlumbingRun {
  wsfu: number;
  gpm: number;
  endPressure: number;
  totalEffectiveLength: number;
  isSafe: boolean;
  failReason: string | null;
}

export interface PlumbingHardwareInventory {
  elbows90: number;
  elbows45: number;
  tees: number;
}

export interface PlumbingCalculatorResults {
  runs: PlumbingRunResult[];
  bom: {
    pipe: Array<{ name: string; feet: number }>;
    fixtures: Array<{ name: string; count: number }>;
    hardware: PlumbingHardwareInventory;
  };
}

// Material Integration Types
export type MaterialUnitType =
  | 'cubicYards'
  | 'gallons'
  | 'squareFeet'
  | 'boxes'
  | 'pounds'
  | 'units'
  | 'linearFeet';

export interface MaterialFromCalculator {
  /** The calculated amount value */
  amount: number;
  /** The unit type (maps to amountType in Material form) */
  amountType: MaterialUnitType;
  /** The measurement system */
  amountSystem: 'imperial' | 'metric';
  /** Suggested name for the material (optional) */
  suggestedName?: string;
  /** Description of what this result represents */
  resultLabel: string;
}

export type OnAddToMaterialCallback = (
  material: MaterialFromCalculator,
) => void;

export type OnAddAllMaterialsCallback = (
  materials: MaterialFromCalculator[],
) => void;

export type OnAddAllMaterialsWithTypeCallback = (
  materials: MaterialFromCalculator[],
  calculatorType: CalculatorType,
) => void;

export const CALCULATOR_CATEGORY_MAP: Record<
  CalculatorType,
  { categoryName: string; icon: string; color: string }
> = {
  [CalculatorType.CONCRETE]: { categoryName: 'Concrete & Aggregates', icon: 'foundation', color: 'gray' },
  [CalculatorType.PAINT]: { categoryName: 'Paint & Coatings', icon: 'format_paint', color: 'blue' },
  [CalculatorType.TILE]: { categoryName: 'Tile & Stone', icon: 'grid_view', color: 'purple' },
  [CalculatorType.FLOORING]: { categoryName: 'Flooring', icon: 'layers', color: 'brown' },
  [CalculatorType.FRAMING]: { categoryName: 'Framing & Lumber', icon: 'carpenter', color: 'orange' },
  [CalculatorType.RETAINING_WALL]: { categoryName: 'Masonry & Retaining', icon: 'fence', color: 'stone' },
  [CalculatorType.ELECTRICAL]: { categoryName: 'Electrical', icon: 'bolt', color: 'yellow' },
  [CalculatorType.PLUMBING]: { categoryName: 'Plumbing', icon: 'plumbing', color: 'teal' },
};